import React, { useState, MouseEvent } from 'react';
import Divider from '@mui/material/Divider';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Menu from '@mui/material/Menu';
import * as Features from '../../../../../../shared/Features';
import history from '../../../../../../shared/history';
import { Translate } from '../../../../../../shared/LanguageService';
import { TranslatedLabels } from '../../../../../../shared/types';
import { useUserContext } from '../../../../../../shared/context/user-context';
import BaseMenuItem from './BaseMenuItem';
import BaseTab from './BaseTab';

type SettingsTabProps = {
    label: string;
};

type TranslatedSettingsMenuProps = {
    open: boolean;
    onClose: () => void;
    anchorElement: HTMLButtonElement | null;
};

type SettingsMenuProps = {
    labels: TranslatedLabels;
} & TranslatedSettingsMenuProps;

const ProductsRoute = '/products';
const ProductTypesRoute = '/producttypes';
const ProductGroupsRoute = '/productgroups';
const RegionsRoute = '/regions';
const AgreementsRoute = '/agreements';
const CustomerAgreementsRoute = '/customer-agreements';
const LegalEntitiesRoute = '/legalentities';
const PlanTemplatesRoute = '/plantemplates';
const CommonTextsRoute = '/commontexts';
const EditingStylesRoute = '/editing-styles';
const TemplatesRoute = '/templates';
const NewsEditorRoute = '/news-editor';
const FeatureFlagsRoute = '/feature-flags';

let routes = [
    ProductsRoute,
    ProductTypesRoute,
    ProductGroupsRoute,
    RegionsRoute,
    AgreementsRoute,
    CustomerAgreementsRoute,
    LegalEntitiesRoute,
    PlanTemplatesRoute,
    CommonTextsRoute,
    EditingStylesRoute,
    TemplatesRoute,
    NewsEditorRoute,
    FeatureFlagsRoute
];

function SettingsMenu({ open, onClose, labels, anchorElement }: SettingsMenuProps) {
    let { user } = useUserContext();

    let shouldShowViewProducts = Features.hasFeature(Features.ViewProducts, user);
    let shouldShowListProductTypes = Features.hasFeature(Features.ListProductTypes, user);
    let shouldShowViewProductGroups = Features.hasFeature(Features.ViewProductGroups, user);
    let shouldShowViewRegions = Features.hasFeature(Features.ViewRegions, user);
    let shouldShowViewAgreements = Features.hasFeature(Features.ViewAgreements, user);
    let shouldShowCustomerAgreements = Features.hasAnyFeature(
        [Features.CreateCustomerAgreements, Features.EditCustomerAgreements],
        user
    );
    let shouldShowListLegalEntities = Features.hasFeature(Features.ListLegalEntity, user);
    let shouldShowCreatePlanTemplate = Features.hasFeature(Features.CreatePlanTemplate, user);
    let shouldShowCreateCommonTexts = Features.hasFeature(Features.CreateCommonTexts, user);
    let shouldShowManageEditingStyles = Features.hasFeature(Features.ManageEditingStyles, user);
    let shouldShowListTemplates = Features.hasFeature(Features.ListTemplates, user);
    let shouldShowNewsEditor = Features.hasFeature(Features.NewsEditor, user);
    let shouldShowManageFeatureFlags = Features.hasFeature(Features.ManageFeatureFlags, user);

    let shouldShowDividerAfterProductSection =
        (shouldShowViewProducts || shouldShowListProductTypes || shouldShowViewProductGroups) &&
        (shouldShowViewRegions ||
            shouldShowViewAgreements ||
            shouldShowCustomerAgreements ||
            shouldShowListLegalEntities ||
            shouldShowCreatePlanTemplate ||
            shouldShowListTemplates ||
            shouldShowCreateCommonTexts ||
            shouldShowManageEditingStyles);

    let shouldShowDividerAfterLegalSection =
        (shouldShowViewRegions ||
            shouldShowViewAgreements ||
            shouldShowCustomerAgreements ||
            shouldShowListLegalEntities) &&
        (shouldShowCreatePlanTemplate ||
            shouldShowListTemplates ||
            shouldShowCreateCommonTexts ||
            shouldShowManageEditingStyles);

    let shouldShowDividerAfterPlanSection =
        (shouldShowViewProducts || shouldShowListProductTypes || shouldShowViewProductGroups) &&
        shouldShowListTemplates;

    let shouldShowDividerAfterTemplatesSection = shouldShowListTemplates && shouldShowNewsEditor;

    let shouldShowDividerBeforeFeatureFlags =
        shouldShowManageFeatureFlags &&
        (shouldShowListTemplates || shouldShowNewsEditor);

    function handleClickViewProducts() {
        onClose();
        history.push(ProductsRoute);
    }

    function handleClickListProductTypes() {
        onClose();
        history.push(ProductTypesRoute);
    }

    function handleClickViewProductGroups() {
        onClose();
        history.push(ProductGroupsRoute);
    }

    function handleClickViewRegions() {
        onClose();
        history.push(RegionsRoute);
    }

    function handleClickViewAgreements() {
        onClose();
        history.push(AgreementsRoute);
    }

    function handleClickCustomerAgreements() {
        onClose();
        history.push(CustomerAgreementsRoute);
    }

    function handleClickListLegalEntities() {
        onClose();
        history.push(LegalEntitiesRoute);
    }

    function handleClickCreatePlanTemplate() {
        onClose();
        history.push(PlanTemplatesRoute);
    }

    function handleClickListTemplates() {
        onClose();
        history.push(TemplatesRoute);
    }

    function handleClickCreateCommonTexts() {
        onClose();
        history.push(CommonTextsRoute);
    }

    function handleClickManageEditingStyles() {
        onClose();
        history.push(EditingStylesRoute);
    }

    function handleClickNewsEditor() {
        onClose();
        history.push(NewsEditorRoute);
    }

    function handleClickFeatureFlags() {
        onClose();
        history.push(FeatureFlagsRoute);
    }

    return (
        <Menu open={open} onClose={onClose} anchorEl={anchorElement}>
            {shouldShowViewProducts ? (
                <BaseMenuItem
                    route={ProductsRoute}
                    onClick={handleClickViewProducts}
                    label={labels.viewProducts}
                />
            ) : null}
            {shouldShowListProductTypes ? (
                <BaseMenuItem
                    route={ProductTypesRoute}
                    onClick={handleClickListProductTypes}
                    label={labels.listProductTypes}
                />
            ) : null}
            {shouldShowViewProductGroups ? (
                <BaseMenuItem
                    route={ProductGroupsRoute}
                    onClick={handleClickViewProductGroups}
                    label={labels.viewProductGroups}
                />
            ) : null}
            {shouldShowDividerAfterProductSection ? <Divider /> : null}
            {shouldShowViewRegions ? (
                <BaseMenuItem
                    route={RegionsRoute}
                    onClick={handleClickViewRegions}
                    label={labels.viewRegions}
                />
            ) : null}
            {shouldShowViewAgreements ? (
                <BaseMenuItem
                    route={AgreementsRoute}
                    onClick={handleClickViewAgreements}
                    label={labels.viewAgreements}
                />
            ) : null}
            {shouldShowCustomerAgreements ? (
                <BaseMenuItem
                    route={CustomerAgreementsRoute}
                    onClick={handleClickCustomerAgreements}
                    label={labels.customerAgreements}
                />
            ) : null}
            {shouldShowListLegalEntities ? (
                <BaseMenuItem
                    route={LegalEntitiesRoute}
                    onClick={handleClickListLegalEntities}
                    label={labels.listLegalEntities}
                />
            ) : null}
            {shouldShowDividerAfterLegalSection ? <Divider /> : null}
            {shouldShowCreatePlanTemplate ? (
                <BaseMenuItem
                    route={PlanTemplatesRoute}
                    onClick={handleClickCreatePlanTemplate}
                    label={labels.createPlanTemplate}
                />
            ) : null}
            {shouldShowCreateCommonTexts ? (
                <BaseMenuItem
                    route={CommonTextsRoute}
                    onClick={handleClickCreateCommonTexts}
                    label={labels.createCommonTexts}
                />
            ) : null}
            {shouldShowManageEditingStyles ? (
                <BaseMenuItem
                    route={EditingStylesRoute}
                    onClick={handleClickManageEditingStyles}
                    label={labels.manageEditingStyles}
                />
            ) : null}
            {shouldShowDividerAfterPlanSection ? <Divider /> : null}
            {shouldShowListTemplates ? (
                <BaseMenuItem
                    route={TemplatesRoute}
                    onClick={handleClickListTemplates}
                    label={labels.listTemplates}
                />
            ) : null}
            {shouldShowDividerAfterTemplatesSection ? <Divider /> : null}
            {shouldShowNewsEditor ? (
                <BaseMenuItem
                    route={NewsEditorRoute}
                    onClick={handleClickNewsEditor}
                    label={labels.newsEditor}
                />
            ) : null}
            {shouldShowDividerBeforeFeatureFlags ? <Divider /> : null}
            {shouldShowManageFeatureFlags ? (
                <BaseMenuItem
                    route={FeatureFlagsRoute}
                    onClick={handleClickFeatureFlags}
                    label={labels.featureFlags}
                />
            ) : null}
        </Menu>
    );
}

function TranslatedSettingsMenu(props: TranslatedSettingsMenuProps) {
    let labels: TranslatedLabels = {
        viewProducts: Translate('header/nav/products'),
        listProductTypes: Translate('header/nav/product-types'),
        viewProductGroups: Translate('header/nav/product-groups'),
        viewRegions: Translate('header/nav/regions'),
        viewAgreements: Translate('header/nav/agreements'),
        customerAgreements: Translate('header/nav/customer-agreements'),
        listLegalEntities: Translate('header/nav/legal-entities'),
        createPlanTemplate: Translate('header/nav/plan-template'),
        listTemplates: Translate('header/nav/templates'),
        createCommonTexts: Translate('header/nav/common-texts'),
        manageEditingStyles: Translate('header/nav/editing-styles'),
        newsEditor: Translate('header/nav/news-editor'),
        featureFlags: Translate('header/nav/feature-flags')
    };

    return <SettingsMenu {...props} labels={labels} />;
}

function SettingsTab({ label, ...props }: SettingsTabProps) {
    let [showMenu, setShowMenu] = useState<boolean>(false);
    let [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        setAnchorElement(event.currentTarget);
        setShowMenu((oldShowMenu) => !oldShowMenu);
    }

    function handleCloseMenu() {
        setShowMenu(false);
    }

    return (
        <>
            <BaseTab
                {...props}
                icon={
                    showMenu ? (
                        <ExpandLessIcon sx={{ width: '24px', height: '24px' }} />
                    ) : (
                        <ExpandMoreIcon sx={{ width: '24px', height: '24px' }} />
                    )
                }
                iconPosition="end"
                onClick={handleClick}
                label={label}
                sx={{ minHeight: '48px', height: '48px' }}
                route={null}
            />
            <TranslatedSettingsMenu
                open={showMenu}
                onClose={handleCloseMenu}
                anchorElement={anchorElement}
            />
        </>
    );
}

function TranslatedSettingsTab(props) {
    let label = Translate('header/nav/dropdown/settings-menu');
    return <SettingsTab {...props} label={label} />;
}

TranslatedSettingsTab.routes = routes;

export default TranslatedSettingsTab;
